"use client";

import { AgentThreadVo } from "@/api";
import { ArrowDownIcon } from "@/assets/icons";
import { ScrollView } from "@/component/ScrollView";
import { fetchUserThreads } from "@/service/threads";
import { threadStore } from "@/store/thread";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { formatTime } from "@/util/datetime";
import { Popover, Spin } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BlurBackgroundContainer } from "../../BlurBackgroundContainer";
import defaultImage from "../../ThreadListItem/default.jpg";

interface ThreadSelectorProps {
  activeThreadId?: string;
  currentThreadId?: string;
  onSelect: (threadId: string) => void;
}

/**
 * A compact version of ThreadListItem for the selector
 */
function CompactThreadItem({
  thread,
  isSelected,
  isCurrent,
}: {
  thread: AgentThreadVo;
  isSelected: boolean;
  isCurrent: boolean;
}) {
  const imageUrl = convertFilePathToUrl(thread.imageUrl || "", {
    level: "medium",
  });

  return (
    <div className="flex flex-col gap-2.5 w-full group">
      <div
        className={classNames(
          "relative w-full aspect-video rounded-lg overflow-hidden transition-all duration-300",
          isSelected ? "ring-2 ring-white/80" : ""
        )}
      >
        <BlurBackgroundContainer
          className="w-full h-full"
          mediaType="image"
          src={imageUrl || defaultImage.src}
          previewSrc={imageUrl || defaultImage.src}
          alt={thread.title || "Thread thumbnail"}
          hoverScale
        />
        {isCurrent && (
          <div className="absolute z-10 inset-0 bg-white/5 flex items-end justify-end right-2 bottom-2 pointer-events-none">
            <div className="bg-white/90 text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg">
              CURRENT
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-0.5 px-0.5">
        <div
          className={classNames(
            "text-[13px] font-semibold truncate transition-colors",
            isSelected
              ? "text-text-primary"
              : "text-text-secondary group-hover:text-text-primary"
          )}
        >
          {thread.title}
        </div>
        <div className="text-[11px] text-text-tertiary truncate opacity-70">
          {thread.updateTime && formatTime(thread.updateTime)}
        </div>
      </div>
    </div>
  );
}

export const ThreadSelector = observer(
  ({ activeThreadId, currentThreadId, onSelect }: ThreadSelectorProps) => {
    const [open, setOpen] = useState(false);
    const [localThreads, setLocalThreads] = useState<AgentThreadVo[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const LIMIT = 12;

    // Unified list of unique threads, pinning current one to top
    const allThreads = useMemo(() => {
      const threadMap = new Map<string, AgentThreadVo>();

      // 1. Add threads from store
      threadStore.threads.forEach((t) => {
        if (t.id) threadMap.set(t.id, t);
      });

      // 2. Add local threads
      localThreads.forEach((t) => {
        if (t.id) threadMap.set(t.id, t);
      });

      // 3. Separate active thread and others
      const activeThread = activeThreadId
        ? threadMap.get(activeThreadId)
        : null;
      if (activeThreadId) threadMap.delete(activeThreadId);

      const others = Array.from(threadMap.values()).sort((a, b) => {
        const timeA = a.updateTime ? new Date(a.updateTime).getTime() : 0;
        const timeB = b.updateTime ? new Date(b.updateTime).getTime() : 0;
        return timeB - timeA;
      });

      const result = [];
      if (activeThread) result.push(activeThread);
      result.push(...others);

      return result;
    }, [threadStore.threads, localThreads, activeThreadId]);

    const currentThread =
      allThreads.find((t) => t.id === currentThreadId) || threadStore.thread;
    const currentTitle = currentThread?.title || "Select Project";

    const loadThreads = useCallback(
      async (isInitial = false) => {
        if (loading) return;
        if (!hasMore && !isInitial) return;

        setLoading(true);
        try {
          const fetchOffset = isInitial ? 0 : offset;
          const result = await fetchUserThreads({
            limit: LIMIT,
            offset: fetchOffset,
          });

          if (isInitial) {
            setLocalThreads(result);
            setOffset(result.length);
          } else {
            setLocalThreads((prev) => {
              const existingIds = new Set(
                prev
                  .map((t) => t.id)
                  .concat(threadStore.threads.map((t) => t.id))
              );
              const filtered = result.filter((t) => !existingIds.has(t.id));
              return [...prev, ...filtered];
            });
            setOffset((prev) => prev + result.length);
          }

          setHasMore(result.length === LIMIT);
        } catch (error) {
          console.error("Failed to fetch threads:", error);
        } finally {
          setLoading(false);
        }
      },
      [loading, hasMore, offset]
    );

    useEffect(() => {
      if (open) {
        if (allThreads.length < LIMIT) {
          void loadThreads(true);
        } else {
          setOffset(allThreads.length);
        }
      }
    }, [open, loadThreads, allThreads.length]);

    const handleScroll = useCallback(
      (node: HTMLElement | null) => {
        if (!node) return;

        const onScroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = node;
          if (
            scrollHeight - scrollTop <= clientHeight + 200 &&
            hasMore &&
            !loading
          ) {
            void loadThreads();
          }
        };

        node.addEventListener("scroll", onScroll);
        return () => node.removeEventListener("scroll", onScroll);
      },
      [hasMore, loading, loadThreads]
    );

    const content = (
      <div className="w-[660px] h-[580px] flex flex-col bg-background-elevated-2 rounded-2xl overflow-hidden shadow-[0_24px_48px_-12px_rgba(0,0,0,0.5)] border border-white/5 backdrop-blur-3xl">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-text-primary text-base font-bold m-0 tracking-tight">
              Select Project
            </h3>
            <p className="text-text-tertiary text-xs font-medium opacity-60">
              Browse assets from your other creations
            </p>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-transparent">
          <ScrollView className="h-full" onScrollableNode={handleScroll}>
            <div className="p-4">
              <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                {allThreads.map((thread) => (
                  <div
                    key={thread.id}
                    className="cursor-pointer"
                    onClick={() => {
                      if (thread.id) {
                        onSelect(thread.id);
                      }
                      setOpen(false);
                    }}
                  >
                    <CompactThreadItem
                      thread={thread}
                      isSelected={thread.id === currentThreadId}
                      isCurrent={thread.id === activeThreadId}
                    />
                  </div>
                ))}
              </div>
              {(loading || hasMore) && (
                <div className="flex justify-center p-12">
                  <Spin size="small" />
                </div>
              )}
              {!hasMore && allThreads.length > 0 && (
                <div className="text-center text-text-tertiary text-[11px] font-medium py-12 opacity-30 tracking-widest uppercase w-full col-span-3">
                  End of Projects
                </div>
              )}
              {!loading && allThreads.length === 0 && (
                <div className="flex flex-col items-center justify-center py-32 text-text-tertiary col-span-3">
                  <div className="text-base font-semibold mb-1 text-center">
                    No projects found
                  </div>
                  <div className="text-sm opacity-60 text-center">
                    Create a project to see it here
                  </div>
                </div>
              )}
            </div>
          </ScrollView>
        </div>
      </div>
    );

    return (
      <Popover
        open={open}
        onOpenChange={setOpen}
        content={content}
        trigger="click"
        placement="bottomLeft"
        classNames={{
          root: "!pt-0",
          body: "!p-0 bg-black! !rounded-lg",
        }}
        arrow={false}
      >
        <button
          type="button"
          className={classNames(
            "flex items-center gap-3 px-4 py-2 rounded-xl transition-all duration-300 border cursor-pointer group h-10 box-border",
            open
              ? "bg-white/10 border-white/20 shadow-inner"
              : "bg-background-card border-white/5 hover:border-white/20 hover:bg-white/[0.04] shadow-sm active:scale-95"
          )}
        >
          <span
            className={classNames(
              "text-[14px] font-semibold truncate max-w-[200px] transition-colors leading-none",
              open
                ? "text-text-primary"
                : "text-text-secondary group-hover:text-text-primary"
            )}
          >
            {currentTitle}
          </span>
          <div
            className={classNames(
              "w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 -mr-1.5",
              open
                ? "text-text-primary"
                : "text-text-tertiary group-hover:text-text-secondary"
            )}
          >
            <ArrowDownIcon
              className={classNames(
                "w-3 h-3 transition-transform duration-300",
                open && "rotate-180"
              )}
            />
          </div>
        </button>
      </Popover>
    );
  }
);
